import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { TFunction } from 'i18next';
import FormsRow from '../../../../components/molecules/Form/components/FormsRow/FormsRow';
import {
  ContentPreview,
  ContentSelectComboBox,
  ListBoxModifier,
  TopContentResult,
} from '../../../../components/molecules/Form/components/ComboBox';
import FormSelect, {
  FormSelectModifier,
  SelectOption,
} from '../../../../components/molecules/Form/components/FormSelect/FormSelect';
import { FormResponseErrorType, FormUtils, useFormContext } from '../../../../components/molecules/Form';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { Assignment } from '../PageToPageAssignmentCard';
import IUrl from '../../../../types/IUrl';
import {
  GetAllAllowedRolesResponse,
  normalizeOption,
  normalizePageToPageOption,
  PageToPageRoleFormOption,
  templateUrl,
  updateAssignmentWithNewRole,
} from '../PageToPage.utils';

import { InteractionStatus } from '../../../../types/InteractionStatus';
import { PageToPageCreateEditCommonTemplate } from './PageToPageCreateEditCommonTemplate';
import { Hint } from '../../../../_lib/components/Hint';
import { RequiredHelpPhrase } from '../../../../_lib/Form2';

type PageToPageCreateFormTemplateProps = {
  templatedPreview: IUrl;
  templatedGetAllAllowedRoles?: IUrl;
};

export function PageToPageCreateFormTemplate({
  templatedPreview,
  templatedGetAllAllowedRoles,
}: PageToPageCreateFormTemplateProps) {
  const { t } = useSonTranslation('PageToPage');

  const [selectedPage, setSelectedPage] = useState<TopContentResult>();
  const [allAllowedRoles, setAllAllowedRoles] = useState<SelectOption[]>();
  const [assignmentPreview, setAssignmentPreview] = useState<Assignment>();

  const { submitActionEnabled, getStatus, inputs, onChange } = useFormContext();

  const resetPageRelevantInformation = () => {
    inputs.roleId.reset(); // as input is held in form state, we need to reset this here too
    setAssignmentPreview(undefined);
    setAllAllowedRoles([]);
  };
  /**
   * resetting page also needs to reset "allAllowedRoles" as they are dependent
   */
  const resetPageAndPageRelevantInformation = () => {
    setSelectedPage(undefined);
    onChange(inputs.assignedPage.name, null);
    resetPageRelevantInformation();
  };

  /**
   * request preview of a single item that will be displayed in the block in default view.
   * need to ask backend, as the suggest route does not contain all data (no color or category)
   */
  const requestPreviewAssigment = useCallback(
    (roleId: string, pageId: string) => {
      axios
        .get<Assignment>(templateUrl({ url: templatedPreview.url, roleId, pageId }))
        .then(response => {
          setAssignmentPreview(response.data);
        })
        .catch();
    },
    [templatedPreview]
  );

  /**
   * To avoid multiple requests, we set the role via state here if we already have an assignmentPreview fetched.
   */
  const requestOrSetAssignmentPreview = useCallback(
    (roleId: string, pageId: string) => {
      if (!assignmentPreview) {
        requestPreviewAssigment(roleId, pageId);
      } else {
        setAssignmentPreview(assignment =>
          assignment ? updateAssignmentWithNewRole(assignment, inputs.roleId.options, roleId) : assignment
        );
      }
    },
    [assignmentPreview, inputs.roleId.options, requestPreviewAssigment]
  );

  const determineInitialFormSelectValue = (allowedRoles: SelectOption[] | undefined) => {
    return allowedRoles?.find(role => role.value === '>listIntegration')?.value || allowedRoles?.at(0)?.value || '';
  };

  /**
   * Fired when a page is selected,
   * depending on the response the initial connection options / roles are filtered.
   */
  const requestGetAllAllowedRolesAndInitiallySetUpPreview = useCallback(
    (pageId: string) => {
      if (templatedGetAllAllowedRoles) {
        axios
          .get<GetAllAllowedRolesResponse>(
            templateUrl({
              url: templatedGetAllAllowedRoles.url,
              pageId,
            })
          )
          .then(response => {
            const newAllowedRoles = normalizePageToPageOption(
              inputs.roleId.options as PageToPageRoleFormOption[] | undefined,
              response.data as unknown as string[] | undefined,
              t as TFunction
            );

            const initialRoleId = determineInitialFormSelectValue(newAllowedRoles);

            // Initially request and set preview:
            requestOrSetAssignmentPreview(initialRoleId, pageId);

            onChange(inputs.roleId.name, initialRoleId);
            setAllAllowedRoles(newAllowedRoles);
          })
          .catch(() => {
            setAllAllowedRoles([]);
          });
      }
    },
    [inputs.roleId.name, inputs.roleId.options, onChange, requestOrSetAssignmentPreview, t, templatedGetAllAllowedRoles]
  );

  const statusMessage = {
    [InteractionStatus.SUCCESS]: FormUtils.createStatusMessage(t('status.create.success')),
    [InteractionStatus.FAIL]: FormUtils.createStatusMessage(t('status.create.fail')),
    [FormResponseErrorType.DUPLICATED_ID]: FormUtils.createStatusMessage(t('status.duplicateId')),
    [FormResponseErrorType.INVALID_SELF_ASSIGNMENT]: FormUtils.createStatusMessage(t('status.invalidSelfAssignment')),
  };

  /**
   * This use effect is solely here because our Form cant handle HTML.
   * -> the FormModel will be initialized with all available roles from the json.
   * -> selecting a page which is only uni-directional will change the select options to only one option
   *    (as we only have one uni-directional option).
   * -> but this will not alter the FormSelect in the FormModel !!!
   * -> FormSelect will render a hidden input in the DOM -> this will be not registered in our Form as we do not
   *    have a hidden input in the json
   * -> this effect therefore sets the input value of the formselect to the unidirectional one.
   * -> HTML Form would make these obsolete
   *    * requestPreviewAssigment muse be fired in useEffect, as the formselect will be readonly.
   */
  useEffect(() => {
    if (selectedPage && (!allAllowedRoles || allAllowedRoles.length === 0)) {
      const uniDirectionalOption = inputs.roleId.options?.filter(option => option.bidirectional === false);
      if (uniDirectionalOption?.length === 1) {
        const roleId = uniDirectionalOption.at(0)?.value;
        requestPreviewAssigment(roleId as string, selectedPage?.id);

        onChange(inputs.roleId.name, roleId);
        setAllAllowedRoles(
          normalizeOption(uniDirectionalOption as PageToPageRoleFormOption[] | undefined, t as TFunction)
        );
      }
    }
  }, [allAllowedRoles, inputs.roleId, onChange, requestPreviewAssigment, selectedPage, t]);

  return (
    <>
      <RequiredHelpPhrase />
      {inputs.assignedPage && (
        <FormsRow>
          <ContentSelectComboBox
            name={inputs.assignedPage.name}
            modifierListBox={ListBoxModifier.RELATIVE}
            required={inputs.assignedPage.required}
            id={inputs.assignedPage.name}
            labelTitle={t('connectNewPage')}
            labelHelpText={t('connectNewPageHelpText')}
            suggestUrl={inputs.assignedPage.comboBoxUrl ? inputs.assignedPage.comboBoxUrl.url : ''}
            onSelection={item => {
              if (item?.id) {
                resetPageRelevantInformation();
                requestGetAllAllowedRolesAndInitiallySetUpPreview(item.id);
                setSelectedPage(item);
                onChange(inputs.assignedPage.name, item.id);
              }
            }}
          />
        </FormsRow>
      )}
      {selectedPage && (
        <ContentPreview
          {...selectedPage}
          reset={() => {
            resetPageAndPageRelevantInformation();
          }}
        />
      )}
      {selectedPage && (
        <FormsRow>
          <FormSelect
            modifier={FormSelectModifier.GROUPED_STRIPES}
            id={inputs.roleId.name}
            name={inputs.roleId.name}
            required={inputs.roleId.required}
            value={inputs.roleId.value}
            labelTitle={t('connectionType')}
            options={allAllowedRoles}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              onChange(event.target.name, event.target.value);
              requestOrSetAssignmentPreview(event.target.value, selectedPage.id);
            }}
          />
        </FormsRow>
      )}
      {!allAllowedRoles ||
        (allAllowedRoles.length === 1 && allAllowedRoles.find(role => role.value.substring(1) === 'complements') && (
          <FormsRow>
            <Hint message={t('warningRightsCheckFailed')} />
          </FormsRow>
        ))}
      <PageToPageCreateEditCommonTemplate
        getStatus={getStatus}
        assignmentPreview={assignmentPreview}
        statusMessages={statusMessage}
        submitActionEnabled={submitActionEnabled}
      />
    </>
  );
}
